import type { PushConfiguration, PushPreferences, PushSubscriptionInput } from '@almanac/contracts';
import { webConfig } from '../config';

export const PUSH_OWNER_KEY = 'almanac.push.owner';
export const DEFAULT_PUSH_PREFERENCES: PushPreferences = {
  research_ready: true,
  digest_ready: false,
};

/** iPadOS can report a Mac user agent, so include its touch capability. */
export function needsHomeScreen(): boolean {
  const ios =
    /iPad|iPhone|iPod/u.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && navigator.standalone === true);
  return ios && !standalone;
}
export function supportsPush(): boolean {
  return (
    window.isSecureContext &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Public VAPID keys are base64url; private keys never enter the browser. */
export function vapidBytes(value: string): Uint8Array<ArrayBuffer> {
  const bytes = atob(
    value.replaceAll('-', '+').replaceAll('_', '/') + '='.repeat((4 - (value.length % 4)) % 4),
  );
  return Uint8Array.from(bytes, (character) => character.charCodeAt(0));
}

/** Preserve response status so removed-device conflicts can return the UI to opt-in. */
export class PushRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** Authenticated browser operations always describe this browser's subscription. */
export function pushClient(getToken: () => Promise<string | null>) {
  async function request<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
    const token = await getToken();
    const response = await fetch(`${webConfig.apiBaseUrl}/api/push${path}`, {
      method,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: AbortSignal.timeout(15_000),
    });
    if (!response.ok) {
      const data = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new PushRequestError(data?.error ?? 'Notification request failed', response.status);
    }
    return response.json() as Promise<T>;
  }
  return {
    configuration: () => request<PushConfiguration>(''),
    status: (endpoint: string) =>
      request<{ enabled: boolean; preferences?: PushPreferences }>('/device/status', 'POST', {
        endpoint,
      }),
    save: (input: PushSubscriptionInput) => request('/device', 'POST', input),
    update: (input: PushSubscriptionInput) => request('/device', 'PUT', input),
    remove: (endpoint: string) => request('/device', 'DELETE', { endpoint }),
    removeAll: () => request('/devices', 'DELETE'),
    test: (endpoint: string) =>
      request<{ configured: boolean; sent: number; failed: number; skipped: number }>(
        '/test',
        'POST',
        { endpoint },
      ),
  };
}

let deviceGeneration = 0;
let deviceOperation: Promise<unknown> = Promise.resolve();

/** Serialize browser subscription changes with logout; stale opt-ins undo their subscription. */
export function withPushDevice<T>(operation: (current: () => boolean) => Promise<T>): Promise<T> {
  const generation = deviceGeneration;
  const next = deviceOperation.then(() => operation(() => generation === deviceGeneration));
  deviceOperation = next.catch(() => undefined);
  return next;
}

/** Unsubscribe before an account change; best-effort server cleanup cannot delay logout. */
export async function clearDevicePush(accessToken?: string): Promise<void> {
  deviceGeneration += 1;
  localStorage.removeItem(PUSH_OWNER_KEY);
  await withPushDevice(async () => {
    if (!('serviceWorker' in navigator)) return;
    try {
      const registration = await navigator.serviceWorker.getRegistration('/');
      if (!registration) return;
      const subscription = await registration.pushManager.getSubscription();
      const notifications = await registration.getNotifications();
      for (const notification of notifications) notification.close();
      if (!subscription) return;
      // Remove browser delivery immediately even if credentials expired or the API is down.
      await subscription.unsubscribe();
      if (accessToken) {
        await fetch(`${webConfig.apiBaseUrl}/api/push/device`, {
          method: 'DELETE',
          headers: { 'content-type': 'application/json', authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
          signal: AbortSignal.timeout(3_000),
        });
      }
    } catch {
      /* Browser/provider expiration is cleaned up on the next send. */
    }
  });
}
